<template>
  <div>
    <!-- 기본정보 -->
    <field-select-module
      v-model="form.stdInfo"
      :board="board"
      :is-print="isPrint"
      :options="[{ type: 'text', label: '문서번호', readonly: true, visibleWhenValue: true }]"
    />

    <!-- 대상자 정보 -->
    <div class="mt-28px">
      <h5 class="mb-4"><i class="dot"></i> 대상자 정보</h5>
      <div class="writeGridContainer">
        <div>
          <div>이름</div>
          <p v-if="isPrint" class="print-input">{{ form.대상자.이름 }}</p>
          <member-selector
            v-else
            popup-type="user"
            v-model="form.대상자.id"
            :member-name="form.대상자.이름"
            :readonly="board.readonly"
            @select="onSelectMember"
          />
        </div>
        <div>
          <div>부서명</div>
          <p v-if="isPrint" class="print-input">{{ form.대상자.부서명 }}</p>
          <ml-input v-else v-model="form.대상자.부서명" :readonly="true" />
        </div>
        <div>
          <div>파트명</div>
          <p v-if="isPrint" class="print-input">{{ form.대상자.파트명 }}</p>
          <ml-input v-else v-model="form.대상자.파트명" :readonly="true" />
        </div>
        <div>
          <div>직급</div>
          <p v-if="isPrint" class="print-input">{{ form.대상자.직급 }}</p>
          <ml-input v-else v-model="form.대상자.직급" :readonly="true" />
        </div>
      </div>
    </div>

    <!-- 입력정보 -->
    <div class="mt-28px">
      <h5 class="mb-4">
        <i class="dot"></i> 입력정보
        <span v-if="!isPrint && !capFieldName" class="section-desc section-desc--danger">사업장을 선택해주세요.</span>
      </h5>
      <!-- 사업장별 인센티브 Cap 대비 현재 발부 현황 (사업장·발급일자 연도 기준, 승인완료 건 합산) -->
      <div v-if="showCapStatus && capStatus.type" class="cap-status" :class="`cap-status--${capStatus.type}`">
        <feather-icon :icon="capStatus.type === 'ok' ? 'CheckCircleIcon' : capStatus.type === 'loading' ? 'LoaderIcon' : 'AlertCircleIcon'" svgClasses="h-4 w-4" />
        <span v-html="capStatus.text"></span>
      </div>
      <div class="writeGridContainer" :class="{ 'section-disabled': !isPrint && !capFieldName }">
        <div class="writeGridLineItem">
          <div class="flex items-center">
            등급
            <feather-icon v-if="!isPrint" icon="InfoIcon" svgClasses="h-5 w-5" class="info-icon" @click="showGradePopup = true" />
          </div>
          <p v-if="isPrint" class="print-input">{{ form.등급 }}</p>
          <div v-else class="h-full flex items-center">
            <radio-group
              v-model="form.등급"
              flex
              :readonly="inputReadonly"
              :items="gradeItems"
            />
          </div>
        </div>
        <div class="writeGridLineItem">
          <div class="flex items-center">
            발급유형
            <feather-icon v-if="!isPrint" icon="InfoIcon" svgClasses="h-5 w-5" class="info-icon" @click="showIssueTypePopup = true" />
          </div>
          <p v-if="isPrint" class="print-input">{{ form.발급유형 }}</p>
          <div v-else class="h-full flex items-center">
            <radio-group
              v-model="form.발급유형"
              flex
              :readonly="inputReadonly"
              :items="['Smart 안전관리', 'Safe 작업환경', 'Strong 안전문화']"
            />
          </div>
        </div>
        <div class="writeGridLineItem">
          <div>세부 설명</div>
          <p v-if="isPrint" class="print-input">{{ form.세부설명 }}</p>
          <ml-textarea v-else class="flex-1 w-full" rows="5" v-model="form.세부설명" :max="2000" :readonly="inputReadonly" placeholder="대상인원 선정사유를 입력해주세요. (필수)" />
        </div>
        <div>
          <div>발급일자</div>
          <p v-if="isPrint" class="print-input">{{ form.발급일자 }}</p>
          <date-selector v-else v-model="form.발급일자" :readonly="inputReadonly" placeholder="날짜선택 (필수)" />
        </div>
      </div>
    </div>

    <!-- 파일첨부 -->
    <file-upload-module id="fileList" class="w-full mt-28px" v-model="form.fileList" :is-print="isPrint" :readonly="board.readonly" />

    <!-- 기준 팝업 -->
    <popup-grade-criteria v-model="showGradePopup" />
    <popup-issue-type-criteria v-model="showIssueTypePopup" />
  </div>
</template>

<script>
import FieldSelectModule from "@/views/modules/FieldSelectModule";
import FileUploadModule from "@/views/modules/FileUploadModule.vue";
import MemberSelector from "@/components/selector/MemberSelector.vue";
import DateSelector from "@/components/selector/DateSelector.vue";
import RadioGroup from "@/components/radio/RadioGroup.vue";
import PopupGradeCriteria from "./PopupGradeCriteria.vue";
import PopupIssueTypeCriteria from "./PopupIssueTypeCriteria.vue";
import { GRADE_SCORE, loadCapSetting, findCap, loadIssuedScoreByField } from "./capUtils";

export default {
  components: {
    FieldSelectModule,
    FileUploadModule,
    MemberSelector,
    DateSelector,
    RadioGroup,
    PopupGradeCriteria,
    PopupIssueTypeCriteria,
  },
  props: {
    isPrint: Boolean,
    board: Object,
    value: Object,
  },
  data() {
    return {
      showGradePopup: false,
      showIssueTypePopup: false,
      // 인센티브 Cap 대비 현재 발부 현황 표시용 (type: '' | loading | ok | over | none | error)
      capStatus: { type: "", text: "", cap: null, issued: 0 },
      capStatusSeq: 0,
      form: {
        stdInfo: {},
        대상자: {
          id: 0,
          이름: "",
          사번: "",
          부서명: "",
          파트명: "",
          직급: "",
          업체id: 0, // 결재선 수신참조 자동 추가 시 사용
          업체명: "",
        },
        등급: "",
        발급유형: "",
        세부설명: "",
        발급일자: "",
        fileList: [],
      },
    };
  },
  watch: {
    value: {
      immediate: true,
      deep: true,
      handler(value) {
        const keys = Object.keys(this.form);
        for (const key of keys) {
          if (value[key] !== undefined && value[key] !== null) {
            this.form[key] = value[key];
          }
        }
      },
    },
    form: {
      immediate: true,
      deep: true,
      handler(value) {
        this.$emit("input", value);
      },
    },
    // 사업장 또는 발급일자 연도가 바뀌면 현재 발부/Cap 현황을 다시 조회
    capStatusKey: {
      immediate: true,
      handler() {
        this.loadCapStatus();
      },
    },
    // 문서 정보가 늦게 로드되어 결재 완료 여부가 바뀌면 표시 여부에 맞춰 재조회
    showCapStatus(value) {
      if (value) this.loadCapStatus();
    },
    // 사업장이 바뀌면 선택했던 등급 초기화 (잔여 Cap 이 사업장마다 달라 이전 선택이 유효하지 않을 수 있음)
    // 최초 로드(빈값 → 값)는 기존 문서의 등급을 지우면 안 되므로 제외
    capFieldName(newValue, oldValue) {
      if (oldValue && newValue !== oldValue && this.form.등급) {
        this.form.등급 = "";
      }
    },
    // 등급만 바뀌면 재조회 없이 문구만 갱신
    "form.등급"() {
      this.refreshCapStatusText();
    },
  },
  computed: {
    capYear() {
      const 발급일자 = this.form.발급일자 || new Date().format("yyyy-MM-dd");
      return `${발급일자}`.substring(0, 4);
    },
    capFieldName() {
      return (this.form.stdInfo && this.form.stdInfo.field_name) || "";
    },
    // 현재 발부/Cap 상태 박스: 작성·결재 진행 중에만 표시. 승인·반려(결재 완료) 문서와 인쇄에서는 숨김
    showCapStatus() {
      return !this.isPrint && !(this.board && this.board.isCompleteApproval);
    },
    // 입력정보 영역: 사업장명을 선택하기 전에는 입력 불가 (Cap 현황이 사업장 기준이므로)
    inputReadonly() {
      return !!(this.board && this.board.readonly) || !this.capFieldName;
    },
    capStatusKey() {
      return `${this.capFieldName}|${this.capYear}`;
    },
    // 등급 선택지: 잔여 Cap 점수(Cap - 현재 발부)를 넘는 등급은 선택 불가.
    // Cap 미설정·조회 실패·조회 중에는 전부 선택 불가 (사업장 미선택 시에는 제한 없음 — 결재품의 시 validateCap 이 최종 차단)
    gradeItems() {
      const status = this.capStatus;
      const base = [
        { label: '개선 (+1점)', value: '개선' },
        { label: '우수 (+2점)', value: '우수' },
        { label: '탁월 (+3점)', value: '탁월' },
      ];
      if (!status.type) return base;
      if (!["ok", "over"].includes(status.type) || status.cap === null) {
        return base.map((item) => ({ ...item, disabled: true }));
      }
      const 잔여 = status.cap - status.issued;
      return base.map((item) => ({ ...item, disabled: GRADE_SCORE[item.value] > 잔여 }));
    },
  },
  methods: {
    // 사업장별 인센티브 Cap 대비 현재 발부 점수 조회 (표시용 — 차단 판정은 validateCap 에서 별도 조회)
    async loadCapStatus() {
      if (!this.showCapStatus) return;
      if (!this.capFieldName) {
        this.capStatus = { type: "", text: "", cap: null, issued: 0 };
        return;
      }
      const seq = ++this.capStatusSeq;
      const fieldName = this.capFieldName;
      const year = this.capYear;
      this.capStatus = { type: "loading", text: `${fieldName} ${year}년 인센티브 Cap 확인 중...`, cap: null, issued: 0 };
      try {
        const projectId = parseInt(this.$route.params.proj_id);
        const [{ capList }, issuedByField] = await Promise.all([
          loadCapSetting(this.$store, projectId, year),
          loadIssuedScoreByField(this.$store, projectId, year, {
            fieldName,
            boardId: parseInt(this.$route.params.boardId) || 1228,
            excludePostId: parseInt(this.$route.params.postId) || 0,
          }),
        ]);
        if (seq !== this.capStatusSeq) return; // 조회 중 사업장/연도가 또 바뀐 경우 무시
        this.capStatus = { type: "ok", text: "", cap: findCap(capList, fieldName), issued: issuedByField[fieldName] || 0 };
        this.refreshCapStatusText();
      } catch (e) {
        if (seq !== this.capStatusSeq) return;
        this.capStatus = { type: "error", text: `${fieldName} ${year}년 인센티브 Cap 정보를 불러오지 못했습니다.`, cap: null, issued: 0 };
      }
    },
    refreshCapStatusText() {
      const status = this.capStatus;
      if (!["ok", "over", "none"].includes(status.type)) return;
      const fieldName = this.capFieldName;
      const year = this.capYear;
      if (status.cap === null) {
        status.type = "none";
        status.text = `<b>${fieldName} ${year}년 Safety Card 인센티브 Cap 미설정</b> — Cap이 설정될 때까지 Safety Card를 발급할 수 없습니다. (현재 발부 ${status.issued}점)`;
        return;
      }
      const 이번점수 = GRADE_SCORE[this.form.등급] || 0;
      const 합계 = status.issued + 이번점수;
      const 잔여 = status.cap - status.issued;
      // 표기 형식: 현재 발부 4/6점 (잔여 2점) · 이번 문서 +2점 → 6/6점 (잔여 0점)  — 분모는 인센티브 Cap
      let text = `${fieldName} ${year}년 인센티브 Cap — <b>현재 발부 ${status.issued}/${status.cap}점</b> (잔여 ${잔여}점)`;
      if (이번점수 > 0) {
        const 적용후잔여 = status.cap - 합계;
        text += ` · 이번 문서 +${이번점수}점 → <b>${합계}/${status.cap}점</b> (${적용후잔여 < 0 ? `${-적용후잔여}점 초과` : `잔여 ${적용후잔여}점`})`;
      }
      if (합계 > status.cap) {
        status.type = "over";
        text += ` — <b>Cap 초과로 발급 불가</b>`;
      } else {
        status.type = "ok";
      }
      status.text = text;
    },
    onSelectMember(member) {
      if (!member) return;
      this.form.대상자.id = member.id;
      this.form.대상자.이름 = member.name || "";
      this.form.대상자.사번 = member.username || "";
      this.form.대상자.부서명 = member.section || "";
      this.form.대상자.파트명 = member.department || "";
      this.form.대상자.직급 = member.rank || "";
      this.form.대상자.업체id = member.company ? member.company.id || 0 : member.company_id || 0;
      this.form.대상자.업체명 = member.company ? member.company.com_name || "" : member.company_name || "";
    },
    async validate() {
      if (!this.form.stdInfo || !this.form.stdInfo.field_name) {
        return "사업장명을 선택해주세요.";
      }
      if (!this.form.대상자.이름) {
        return "대상자를 선택해주세요.";
      }
      if (!this.form.등급) {
        return "등급을 선택해주세요.";
      }
      if (!this.form.발급유형) {
        return "발급유형을 선택해주세요.";
      }
      if (!this.form.세부설명) {
        return "세부 설명(대상인원 선정사유)을 입력해주세요.";
      }
      // 발급일자는 작성자가 직접 입력(필수). 결재자 차례(수정 불가)에는 수동 입력 전환 이전 문서가 막히지 않도록 검사하지 않는다
      if (!this.form.발급일자 && !(this.board && this.board.isTurnApproval)) {
        return "발급일자를 선택해주세요.";
      }
      // 사업장별 연간 Cap 초과 검사 (상신 시 + 각 결재 승인 시 모두 호출됨)
      return await this.validateCap();
    },
    async validateCap() {
      // 점수 귀속 연도 = 발급일자(작성자 입력) 기준 — 1227 집계와 동일
      // 발급일자가 없는 전환 이전 문서는 최종 승인 시 승인일로 보정되므로 오늘 기준으로 검사한다
      const year = this.capYear;
      const fieldName = this.capFieldName;
      const projectId = parseInt(this.$route.params.proj_id);

      let cap = null;
      let 누적점수 = 0;
      try {
        // 연도별 사업장 Cap 설정(1231) + 해당 사업장의 결재완료 건 누적 점수(현재 문서 자신은 제외 — 이중계산 방지)
        const [{ capList }, issuedByField] = await Promise.all([
          loadCapSetting(this.$store, projectId, year),
          loadIssuedScoreByField(this.$store, projectId, year, {
            fieldName,
            boardId: parseInt(this.$route.params.boardId) || 1228,
            excludePostId: parseInt(this.$route.params.postId) || 0,
          }),
        ]);
        cap = findCap(capList, fieldName);
        누적점수 = issuedByField[fieldName] || 0;
      } catch (e) {
        // 1231 은 전원 읽기 가능하므로 조회 실패는 통신 오류 — Cap 확인 없이는 가점을 진행시키지 않는다 (2026-10 고객 요청)
        return `${fieldName} ${year}년 Safety Card 인센티브 Cap 정보를 확인하지 못해 진행할 수 없습니다.<br/>잠시 후 다시 시도해주세요. 계속 반복되면 관리자에게 문의해주세요.`;
      }

      // Cap 미설정(연도 게시글 없음 또는 해당 사업장 점수 비어 있음)이면 가점 진행 차단 — 2026-10 고객 요청
      if (cap === null) {
        return `${fieldName} ${year}년 Safety Card 인센티브 Cap이 아직 설정되지 않아 발급을 진행할 수 없습니다.`;
      }

      const 이번점수 = GRADE_SCORE[this.form.등급] || 0;
      if (누적점수 + 이번점수 > cap) {
        return `${fieldName} ${year}년 Safety Card 인센티브 Cap(${cap}점)을 초과하여 발급을 진행할 수 없습니다.<br/>현재 발부 ${누적점수}점 + 이번 문서 ${이번점수}점 = ${누적점수 + 이번점수}점 (Cap ${cap}점)`;
      }
    },
    // 결재 최종 승인 시 문서번호 자동 입력 (발급일자는 작성자가 직접 입력, 연도는 발급일자 기준)
    async prevSaveEvent(post) {
      try {
        if (this.isFinalApprovalTurn() && post.body) {
          // 발급일자 수동 입력 전환 이전에 상신된 문서(발급일자 없음)는 기존 방식대로 최종 승인일로 채운다
          if (!post.body.발급일자) {
            post.body.발급일자 = new Date().format("yyyy-MM-dd");
          }
          if (post.body.stdInfo && !post.body.stdInfo.문서번호) {
            post.body.stdInfo.문서번호 = await this.generateDocNumber(post, post.body.발급일자);
          }
        }
      } catch (e) {
        /* ignore */
      }
      return post;
    },
    isFinalApprovalTurn() {
      if (!this.board || !this.board.isTurnApproval) return false;
      const line = this.board.approvalLine || [];
      // 처리부서 결재 이력은 line에 document_company_id 가 붙어서 들어오므로 일반 결재선과 분리한다
      const approvals = line.filter((member) => member.type_code === "approval" && !member.document_company_id);
      const documentCompany = this.board.documentCompany;
      const hasDocumentCompany = !!(documentCompany && (documentCompany.document_company_id || documentCompany.virtual_company_id));
      const documentCompanyDone = line.some((member) => member.document_company_id);

      if (hasDocumentCompany && !documentCompanyDone) {
        // 처리부서 결재가 마지막 단계: 일반 결재선 전원 승인 + 내(처리부서) 차례일 때가 최종 승인
        return approvals.length > 0 && approvals.every((member) => parseInt(member.status_id) === 2);
      }

      // 처리부서가 없으면 일반 결재선의 마지막 결재자 승인이 최종
      if (approvals.length === 0) return false;
      const last = approvals[approvals.length - 1];
      const myId = this.$store.state.auth.userInfo.id;
      if (parseInt(last.user_id) !== parseInt(myId)) return false;
      return approvals.slice(0, -1).every((member) => parseInt(member.status_id) === 2);
    },
    async generateDocNumber(post, issueDate) {
      const year = `${issueDate}`.substring(0, 4);
      const dept = (post.body.stdInfo && (post.body.stdInfo.section || post.body.stdInfo.department)) || "";
      const prefix = `DKS-세이프티카드-${year}-${dept}-`;
      let seq = 1;
      try {
        // 같은 접두어의 기존 문서번호 중 최대값 + 1 로 채번 (건수 기반은 삭제/캐시에 취약)
        const response = await this.$store.dispatch("board/LOAD_POST_LIST", {
          projectId: parseInt(this.$route.params.proj_id),
          boardId: parseInt(this.$route.params.boardId) || 1228,
          page: 1,
          perPage: 1,
          conditions: [{ key: "body.stdInfo.문서번호", operator: "LIKE", value: `${prefix}%` }],
          orderTarget: "$body.stdInfo.문서번호",
          orderDirection: "desc",
          cacheBuster: Date.now(),
        });
        if (response.data && response.data.length > 0) {
          const body = JSON.parse(response.data[0].post_content).body || {};
          const lastNumber = (body.stdInfo && body.stdInfo.문서번호) || "";
          const lastSeq = parseInt(lastNumber.substring(prefix.length), 10);
          seq = isNaN(lastSeq) ? (response.total || 0) + 1 : lastSeq + 1;
        }
      } catch (e) {
        /* ignore */
      }
      return `${prefix}${String(seq).padStart(3, "0")}`;
    },
  },
};
</script>

<style lang="scss" scoped>
.section-desc {
  font-size: 13px;
  font-weight: 400;
  color: #888;
  margin-left: 6px;

  &--danger {
    color: #e42728;
  }
}
.section-disabled {
  opacity: 0.55;
  pointer-events: none;
}
.info-icon {
  color: #7e72f2;
  cursor: pointer;
  margin-left: 6px;
}
.cap-status {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  padding: 10px 14px;
  border-radius: 6px;
  font-size: 13px;
  line-height: 1.5;
  border: 1px solid #e0e0e0;
  background: #f7f8fa;
  color: #555;

  &--ok {
    border-color: #b7e4c7;
    background: #f0faf3;
    color: #2d7a4b;
  }
  &--over,
  &--none,
  &--error {
    border-color: #f5b5b5;
    background: #fdecec;
    color: #e42728;
  }
}
</style>
